<script setup lang="ts">
import { computed, getCurrentInstance } from 'vue';
import { useRouter } from 'vue-router/composables';
import FaAngleLeft from './icons/FaAngleLeft.vue';
import ArticleSearch from './ArticleSearch';
import { createBackgroundStyle, createTextStyle } from '@/common/customisation/utils/styleUtils';
import MinimizeIcon from './icons/MinimizeIcon.vue';
import { Bridge } from '@/common/bridge';
import { analytics, COMPONENT_EVENTS } from '@/common/logs';
import { useI18n } from '@/plugins/i18n';
import { useWidgetConversation } from '@/stores/useWidgetConversation';
import { storeToRefs } from 'pinia';

interface HeaderProps {
  backgroundColor?: string;
  isVisible?: boolean;
}

interface TitleProps {
  textContent?: string;
  textColor?: string;
  isVisible?: boolean;
}

interface NavIconProps {
  actionColor?: string;
  isVisible?: boolean;
}

interface SearchProps {
  placeholderText?: string;
  isVisible?: boolean;
}

interface Props {
  header?: HeaderProps;
  title?: TitleProps;
  navIcon1?: NavIconProps;
  navIcon2?: NavIconProps;
  search?: SearchProps;
  defaultTitle?: string;
}

const props = withDefaults(defineProps<Props>(), {
  header: undefined,
  title: undefined,
  navIcon1: undefined,
  navIcon2: undefined,
  search: undefined,
  defaultTitle: undefined,
});

const emit = defineEmits<{
  (e: 'back'): void;
}>();

const instance = getCurrentInstance();
const router = useRouter();

const widgetConversation = useWidgetConversation();
const { supportWizard } = storeToRefs(widgetConversation);

const isVoiceCallActive = computed(() => {
  return !!supportWizard.value?.support.voice.voiceChatId;
});

const hasEventListener = (eventName: string): boolean => {
  if (!instance) return false;
  // In Vue 2.7, listeners are available via $listeners on the proxy
  const listeners = (instance.proxy as any)?.$listeners;
  return !!(listeners && eventName in listeners);
};

const backgroundStyle = computed(() => {
  if (!props.header?.isVisible) {
    return {
      display: 'none',
    };
  }
  return createBackgroundStyle(props.header.backgroundColor);
});

const titleStyle = computed(() => {
  return createTextStyle(props.title?.textColor);
});

const navIcon1Style = computed(() => {
  return createTextStyle(props.navIcon1?.actionColor);
});

const navIcon2Style = computed(() => {
  return createTextStyle(props.navIcon2?.actionColor);
});

const searchVisible = computed(() => {
  return props.search?.isVisible;
});

const placeholderText = computed(() => {
  return props.search?.placeholderText;
});

const handleBackClick = () => {
  analytics.trackClick({
    name: COMPONENT_EVENTS.HEADER_BACK,
  });

  emit('back');
  if (!hasEventListener('back')) {
    router.back();
  }
};

const handleMinimizeClick = () => {
  Bridge.minimize({ inCall: isVoiceCallActive.value });
};
</script>

<template>
  <header class="help-header" :style="backgroundStyle">
    <div class="header-top">
      <div class="header-top-left">
        <button v-if="navIcon1?.isVisible" class="nav-button" aria-label="Back" tabindex="0" @click="handleBackClick">
          <FaAngleLeft :style="navIcon1Style" />
        </button>

        <h1 v-if="title?.isVisible" class="header-title theme-font-h2" :style="titleStyle">
          {{ title?.textContent || defaultTitle }}
        </h1>
      </div>

      <button
        v-if="navIcon2?.isVisible"
        class="close-button"
        aria-label="Close"
        tabindex="0"
        @click="handleMinimizeClick"
      >
        <MinimizeIcon :style="navIcon2Style" />
      </button>
    </div>

    <ArticleSearch v-if="searchVisible" :placeholder-text="placeholderText" />
  </header>
</template>

<style scoped lang="scss">
.help-header {
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.header-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
}

.header-top-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-title {
  flex: 1;
  margin: 0;
}

.nav-button,
.close-button {
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  transition: opacity 0.2s ease;
  flex-shrink: 0;
  padding: 0;
  outline: none;

  &:hover {
    opacity: 0.7;
  }
}

.nav-button {
  svg {
    width: 26px;
    height: 26px;
  }
}

.close-button {
  svg {
    width: 20px;
    height: 20px;
  }
}
</style>
